import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import type { Client } from '@jepto/shared/models/client.model';
import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';

const queryRouteClient = `
query getClient($clientId: String!) {
    getClient(clientId: $clientId) {
        id
        sortKey
        name
        logo
        isArchived
        isDeleted
    }
}
`;

type RouteClient = Client & { isArchived?: boolean; isDeleted?: boolean };

/**
 * `missing`: the API has no live client with that id (`getClient` is null or
 * the client is deleted). `unknown`: the request failed, so nothing is known.
 */
export type RouteClientLookup =
	| { status: 'found'; client: RouteClient }
	| { status: 'missing' }
	| { status: 'unknown' };

/** Resolves a `/clients/:id` route id the dropdown's active list does not hold. */
@Injectable({ providedIn: 'root' })
export class RouteClientLookupService {
	private readonly jepRequest = inject(JepRequestService);

	lookup(clientId: string): Observable<RouteClientLookup> {
		return this.jepRequest
			.query({
				query: queryRouteClient,
				variables: { clientId },
				fetchPolicy: 'network-only',
			})
			.pipe(
				map(
					(response: {
						data?: { getClient?: RouteClient | null };
					}): RouteClientLookup => {
						const client = response?.data?.getClient;
						return client && !client.isDeleted
							? { status: 'found', client }
							: { status: 'missing' };
					}
				),
				catchError(() => of<RouteClientLookup>({ status: 'unknown' }))
			);
	}
}
